import axios from 'axios'
import { authService } from '../services/authService'
import { isTokenInvalidResponse } from '../services/request'
import store from '../store'
import keycloak, { isSsoSession } from '../keycloak'

// For the new API (v2)
export const cortexApi = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
})

// For the legacy API
export const legacyApi = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor (for adding auth tokens, etc.). Mirrors
// services/request.ts: keeps SSO tokens fresh at request time (the
// provider's onTokenExpired timer does not fire in throttled background
// tabs or across laptop sleep).
const addAuthToken = async (config) => {
  if (isSsoSession() && keycloak.authenticated) {
    try {
      await keycloak.updateToken(30)
    } catch {
      // Refresh failed — session teardown is handled by the Keycloak event
      // handler; let the request proceed and fail through 401 handling.
    }
  }

  // Add auth token if available
  let token = localStorage.getItem('token')
  if (token === 'undefined' || token === 'null') token = null
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  } else {
    // If there's no token, don't send "Bearer null"
    delete config.headers.Authorization
  }
  return config
}

legacyApi.interceptors.request.use(addAuthToken, (error) =>
  Promise.reject(error)
)

cortexApi.interceptors.request.use(addAuthToken, (error) =>
  Promise.reject(error)
)

// Response interceptor (for handling errors globally). Mirrors
// services/request.ts: one forced refresh + retry for SSO sessions before
// giving up, and a LOCAL-only logout — endSsoSession=false, an automatic
// 401 must never escalate into a realm-wide Keycloak single logout.
const handle401 = (apiInstance) => async (error) => {
  // Catch 401 errors but only log the user out when the TOKEN itself is invalid,
  // not when a specific resource is unauthorized for an otherwise-valid session.
  if (error.response?.status === 401) {
    const retriableConfig = error.config
    if (
      isSsoSession() &&
      keycloak.authenticated &&
      retriableConfig &&
      !retriableConfig.kcRetried
    ) {
      try {
        await keycloak.updateToken(-1)
        // Re-read from localStorage (single source of truth — onTokens may
        // have declined the write on a concurrent teardown). No manual
        // header write here: addAuthToken runs again on the retry and some
        // 401 configs have no headers object at all.
        const freshToken = localStorage.getItem('token')
        if (isSsoSession() && freshToken) {
          retriableConfig.kcRetried = true
          return apiInstance.request(retriableConfig)
        }
      } catch {
        // Refresh failed — fall through to the invalid-token handling below.
      }
    }

    // Shared predicate with services/request.ts — the backend signals dead
    // tokens via `code` ('invalid_token', 'token_expired', ...); the old
    // local heuristic here missed those shapes entirely.
    if (isTokenInvalidResponse(error.response?.data)) {
      // Token is bad: wipe the LOCAL session only. The store reset makes
      // App.tsx fall back to the login routes — no manual redirect needed.
      authService.logout(false)
      store.dispatch.profile.setProfile({})
    }
    // Otherwise: leave the session intact, let the caller handle the 401
  }
  return Promise.reject(error)
}

legacyApi.interceptors.response.use(
  (response) => response,
  handle401(legacyApi)
)

cortexApi.interceptors.response.use(
  (response) => response,
  handle401(cortexApi)
)
